import { PAGE_EDITOR_IDS } from '@/components/admin/experiences/page-editor/contracts';
import { VariableNameField } from '@/components/admin/experiences/page-editor/variable-name-field';
import { TooltipShield } from '@/components/admin/tooltip-shield';
import { PaintedTextField } from '@/components/admin/variables/painted-text-field';
import { VariableMenu } from '@/components/admin/variables/variable-menu';
import {
  addSelectionOption,
  moveSelectionOption,
  removeSelectionOption,
  repeatedSelectionOption,
} from '@/lib/page-editor';
import type { PaletteContent } from '@/lib/variable-palette';
import { firstPaletteToken, paletteFullSentence } from '@/lib/variable-palette';
import type { VariableZone } from '@/lib/variable-pill';
import type { ExperienceBlockDraft } from '@matter_extractor/shared';
import {
  EXPERIENCE_NOTE_QUESTION_MAX,
  EXPERIENCE_NOTE_STARS,
  EXPERIENCE_SELECTION_OPTIONS_MAX,
  EXPERIENCE_SELECTION_OPTIONS_MIN,
  EXPERIENCE_SELECTION_OPTION_MAX,
  EXPERIENCE_SELECTION_QUESTION_MAX,
} from '@matter_extractor/shared';
import { Checkbox, Icon, TextField, Tooltip } from '@pms-digital/design-system';
import type {
  CSSProperties,
  DragEvent as ReactDragEvent,
  KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { useId, useState } from 'react';

/**
 * Les inspecteurs des blocs Sélection et Note - l'onglet **Contenu** (retour-
 * experience.md §4, §7.2 ; maquette `editeur-page-retour.html` ; issue #176).
 *
 * Deux panneaux, un fichier : ils partagent la question - le champ peint des
 * variables, comme le libellé d'une Checkbox, 200 caractères - la case
 * « Obligatoire » et le nom de variable, et ne diffèrent que par ce qu'ils
 * portent en plus : une liste d'options pour la Sélection, une ligne « Échelle »
 * qui ne se règle pas pour la Note (« comparé à la Sélection, l'inspecteur perd
 * la liste d'options et rien d'autre »).
 *
 * ## La liste d'options : une ligne chacune, deux bornes qui se disent
 *
 * Une poignée, un champ de 40 caractères, une croix. « Ajouter une option » se
 * pose dessous, et les bornes de 2 à 5 se lisent à l'écran avant l'enregistrement
 * (maquette, choix 10) : à deux options les croix sont fermées et disent
 * pourquoi au survol, à cinq « Ajouter une option » l'est et une phrase le dit
 * sous la liste, et un libellé écrit deux fois borde les deux champs de rouge
 * avec une phrase rouge. L'option VIDE n'est pas rouge : un bloc neuf en porte
 * deux, et le rouge dès la naissance se lirait comme une faute que personne n'a
 * encore commise ; une phrase grise dit qu'elle sera refusée.
 *
 * Les options n'ont pas d'identifiant, et le rang est leur adresse : la valeur
 * écrite en session est le LIBELLÉ retenu (§4.1), jamais un indice.
 *
 * ## Glisser NATIF, plus le clavier
 *
 * La poignée est la SOURCE du glisser et la ligne la cible, pour la raison que
 * donne `global-rules.tsx` : une ligne `draggable` démarrerait un glisser au
 * clic dans le champ. Les flèches haut et bas déplacent d'un cran, comme la
 * poignée d'un bloc de la pile.
 *
 * ## L'aperçu des étoiles est ÉTEINT, au thème de la page
 *
 * La ligne « Échelle » montre cinq étoiles au contour de la couleur du texte du
 * thème, sur son fond (maquette, choix 16) : le thème arrive en variables CSS
 * `--lecteur-*` que le corps de l'éditeur calcule avec le lecteur, et l'aperçu
 * ne se clique pas - c'est l'aperçu du centre qui montre la page.
 */

const LABEL = 'block font-semibold text-gray-800 text-caption';
const HINT = 'text-gray-600 text-caption leading-4';

type SelectionBlock = Extract<ExperienceBlockDraft, { type: 'selection' }>;
type NoteBlock = Extract<ExperienceBlockDraft, { type: 'note' }>;

interface FieldsProps {
  readonly readOnly: boolean;
  /** The `@` menu of THIS page, for the letters typed after the `@` (D3). */
  readonly palette: (query: string) => PaletteContent;
  /** What each token IS on this page, for the pill and its fiche (D5, D6). */
  readonly zone: VariableZone;
  /** The name is declared twice or more in the timeline (§8.6). */
  readonly duplicateName: boolean;
}

/** « Question » - the painted field of the variable module, shared by both blocks. */
function QuestionField({
  block,
  max,
  readOnly,
  palette,
  zone,
  onChange,
}: Pick<FieldsProps, 'readOnly' | 'palette' | 'zone'> & {
  readonly block: SelectionBlock | NoteBlock;
  readonly max: number;
  readonly onChange: (question: string) => void;
}) {
  const labelId = useId();
  return (
    <div className="space-y-1.5">
      <p id={labelId} className={LABEL}>
        Question
      </p>
      <PaintedTextField
        value={block.question}
        readOnly={readOnly}
        placeholder=""
        ariaLabelledBy={labelId}
        testId={PAGE_EDITOR_IDS.blockLabel}
        insertTestId={null}
        minHeightClass="min-h-16"
        zone={zone}
        renderMenu={(query, insert, fits) => (
          <VariableMenu
            content={palette(query ?? '')}
            query={query}
            onInsert={insert}
            fits={fits}
            full={paletteFullSentence(max)}
          />
        )}
        firstToken={(query) => firstPaletteToken(palette(query))}
        // The counter is the bound, refused and never cut (#160).
        counter={{ max }}
        onChange={onChange}
      />
      <p className={HINT}>Variables acceptées : tape @ pour en insérer une.</p>
    </div>
  );
}

/** « Obligatoire » - the box and the sentence that says what it costs the person. */
function RequiredBox({
  checked,
  readOnly,
  sentence,
  onChange,
}: {
  readonly checked: boolean;
  readonly readOnly: boolean;
  readonly sentence: string;
  readonly onChange: (required: boolean) => void;
}) {
  return (
    <Checkbox
      label="Obligatoire"
      description={sentence}
      data-testid={PAGE_EDITOR_IDS.blockRequired}
      checked={checked}
      disabled={readOnly}
      onCheckedChange={onChange}
    />
  );
}

/** What a drag of an option is carrying and where it would land. */
type OptionDrag = { readonly from: number; readonly over: number | null; readonly after: boolean };

export interface SelectionPanelProps extends FieldsProps {
  readonly block: SelectionBlock;
  readonly onChange: (next: ExperienceBlockDraft) => void;
}

export function SelectionPanel({
  block,
  readOnly,
  palette,
  zone,
  duplicateName,
  onChange,
}: SelectionPanelProps) {
  const [drag, setDrag] = useState<OptionDrag | null>(null);
  const { options } = block;
  const repeated = repeatedSelectionOption(options);
  const atMin = options.length <= EXPERIENCE_SELECTION_OPTIONS_MIN;
  const atMax = options.length >= EXPERIENCE_SELECTION_OPTIONS_MAX;
  const hasEmpty = options.some((option) => option.trim() === '');

  const write = (next: string[]): void => onChange({ ...block, options: next });

  /** The drop: `to` is read in the list WITHOUT the dragged row, as `global-rules.tsx` does. */
  function drop(target: number): void {
    if (drag === null) return;
    const { from, after } = drag;
    setDrag(null);
    if (from === target) return;
    let to = target;
    if (from < to) to -= 1;
    write(moveSelectionOption(options, from, after ? to + 1 : to));
  }

  function onHandleKey(event: ReactKeyboardEvent<HTMLSpanElement>, rank: number): void {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    event.preventDefault();
    write(moveSelectionOption(options, rank, rank + (event.key === 'ArrowUp' ? -1 : 1)));
  }

  return (
    <div className="space-y-3">
      <QuestionField
        block={block}
        max={EXPERIENCE_SELECTION_QUESTION_MAX}
        readOnly={readOnly}
        palette={palette}
        zone={zone}
        onChange={(question) => onChange({ ...block, question })}
      />

      <div className="space-y-1.5">
        <p className={LABEL}>Options</p>
        <ol data-testid={PAGE_EDITOR_IDS.selectionOptions} className="space-y-1.5">
          {options.map((option, rank) => {
            const marked = repeated?.ranks.includes(rank) === true;
            const hint = drag !== null && drag.over === rank;
            return (
              <li
                // biome-ignore lint/suspicious/noArrayIndexKey: an option has no identity but its rank (§4.1).
                key={rank}
                data-testid={PAGE_EDITOR_IDS.selectionOption}
                onDragOver={(event: ReactDragEvent<HTMLElement>) => {
                  if (drag === null) return;
                  event.preventDefault();
                  const box = event.currentTarget.getBoundingClientRect();
                  const after = event.clientY > box.top + box.height / 2;
                  setDrag((was) => (was === null ? null : { ...was, over: rank, after }));
                }}
                onDrop={(event: ReactDragEvent<HTMLElement>) => {
                  event.preventDefault();
                  drop(rank);
                }}
                className={`flex items-center gap-1.5 border-2 ${
                  hint
                    ? `border-bleu-nuit ${drag.after ? 'border-b-4' : 'border-t-4'}`
                    : 'border-transparent'
                } ${drag?.from === rank ? 'opacity-40' : ''}`}
              >
                {readOnly ? null : (
                  <Tooltip content="Glisser pour réordonner, ou les flèches haut et bas">
                    {/* The drag SOURCE and the only one: a draggable row would start a
                        drag on a click inside the field. */}
                    {/* biome-ignore lint/a11y/useSemanticElements: a handle, not a form control. */}
                    <span
                      role="button"
                      tabIndex={0}
                      draggable
                      data-testid={PAGE_EDITOR_IDS.selectionOptionHandle}
                      aria-label={`Déplacer l’option ${rank + 1} - flèches haut et bas`}
                      onDragStart={() => setDrag({ from: rank, over: null, after: false })}
                      onDragEnd={() => setDrag(null)}
                      onKeyDown={(event) => onHandleKey(event, rank)}
                      className="flex shrink-0 cursor-grab rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
                    >
                      <span className="inline-flex text-gray-300">
                        <Icon name="drag_indicator" size={16} />
                      </span>
                    </span>
                  </Tooltip>
                )}
                {/* A repeated label borders the field in red (`status`, which sets
                    `aria-invalid`); the sentence that says why stays under the
                    list, shared by the two rows it names. */}
                <div className="min-w-0 flex-1">
                  <TextField
                    label={`Option ${rank + 1}`}
                    hideLabel
                    compact
                    value={option}
                    readOnly={readOnly}
                    maxLength={EXPERIENCE_SELECTION_OPTION_MAX}
                    status={marked ? 'error' : 'default'}
                    onChange={(event) => {
                      const value = event.currentTarget.value;
                      write(options.map((one, at) => (at === rank ? value : one)));
                    }}
                  />
                </div>
                {readOnly ? null : (
                  <Tooltip
                    content={
                      atMin
                        ? 'Une Sélection garde au moins deux options.'
                        : `Retirer l’option ${rank + 1}`
                    }
                  >
                    <TooltipShield closed={atMin}>
                      <button
                        type="button"
                        data-testid={PAGE_EDITOR_IDS.selectionOptionRemove}
                        aria-label={`Retirer l’option ${rank + 1}`}
                        disabled={atMin}
                        onClick={() => write(removeSelectionOption(options, rank))}
                        className="inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                      >
                        <Icon name="close" size={16} />
                      </button>
                    </TooltipShield>
                  </Tooltip>
                )}
              </li>
            );
          })}
        </ol>

        <button
          type="button"
          data-testid={PAGE_EDITOR_IDS.selectionOptionAdd}
          disabled={readOnly || atMax}
          onClick={() => write(addSelectionOption(options))}
          className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-md border-2 border-gray-300 border-dashed bg-white px-3 py-1.5 font-semibold text-gray-700 text-caption transition-colors hover:border-bleu-nuit hover:text-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Icon name="add" size={14} />
          Ajouter une option
        </button>

        {repeated !== null ? (
          <p
            role="alert"
            data-testid={PAGE_EDITOR_IDS.selectionOptionsNote}
            className="text-rouge-deep text-caption leading-4"
          >
            {repeated.ranks.length === 2
              ? 'Deux options portent'
              : `${repeated.ranks.length} options portent`}{' '}
            « {repeated.label} ». L’enregistrement d’une version sera refusé tant qu’elles se
            répètent.
          </p>
        ) : atMax ? (
          <p data-testid={PAGE_EDITOR_IDS.selectionOptionsNote} className={HINT}>
            Une Sélection porte au plus {EXPERIENCE_SELECTION_OPTIONS_MAX} options.
          </p>
        ) : (
          <p data-testid={PAGE_EDITOR_IDS.selectionOptionsNote} className={HINT}>
            De {EXPERIENCE_SELECTION_OPTIONS_MIN} à {EXPERIENCE_SELECTION_OPTIONS_MAX} options,{' '}
            {EXPERIENCE_SELECTION_OPTION_MAX} caractères chacune, sans variable ; deux options ne
            portent pas le même libellé. Glisse une option pour la réordonner.
            {hasEmpty ? ' Une option vide est refusée à l’enregistrement.' : ''}
          </p>
        )}
      </div>

      <RequiredBox
        checked={block.required}
        readOnly={readOnly}
        sentence="Une page ne se quitte pas sans une option allumée, et la personne ne peut plus éteindre la sienne."
        onChange={(required) => onChange({ ...block, required })}
      />

      <VariableNameField
        value={block.variableName}
        readOnly={readOnly}
        duplicate={duplicateName}
        onChange={(variableName) => onChange({ ...block, variableName })}
      />
    </div>
  );
}

export interface NotePanelProps extends FieldsProps {
  readonly block: NoteBlock;
  /** The theme of the page as CSS variables of the reader, for the preview of the stars (§7.2). */
  readonly previewStyle?: CSSProperties | undefined;
  readonly onChange: (next: ExperienceBlockDraft) => void;
}

export function NotePanel({
  block,
  readOnly,
  palette,
  zone,
  duplicateName,
  previewStyle,
  onChange,
}: NotePanelProps) {
  return (
    <div className="space-y-3">
      <QuestionField
        block={block}
        max={EXPERIENCE_NOTE_QUESTION_MAX}
        readOnly={readOnly}
        palette={palette}
        zone={zone}
        onChange={(question) => onChange({ ...block, question })}
      />

      <div className="space-y-1.5">
        <p className={LABEL}>Échelle</p>
        {/* Five stars, switched OFF, in the theme of the page. They do not click: the
            preview of the centre is what shows the page (maquette, choix 16). */}
        <span
          role="img"
          data-testid={PAGE_EDITOR_IDS.noteScale}
          aria-label={`Aperçu : ${EXPERIENCE_NOTE_STARS} étoiles`}
          style={previewStyle}
          className="inline-flex gap-1.5 rounded-md border border-gray-200 bg-[var(--lecteur-color-background,var(--bg-surface))] px-2 py-1.5 text-[var(--lecteur-color-text,var(--text-secondary))]"
        >
          {Array.from({ length: EXPERIENCE_NOTE_STARS }, (_, rank) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: five identical stars, never reordered.
            <Icon key={rank} name="star" size={24} />
          ))}
        </span>
        <p className={HINT}>
          Toujours cinq étoiles. La note écrite est un entier de 1 à 5 ; Sessions la lit « 4 / 5 ».
        </p>
      </div>

      <RequiredBox
        checked={block.required}
        readOnly={readOnly}
        sentence="Une page ne se quitte pas sans une étoile donnée, et la personne ne peut plus éteindre sa note."
        onChange={(required) => onChange({ ...block, required })}
      />

      <VariableNameField
        value={block.variableName}
        readOnly={readOnly}
        duplicate={duplicateName}
        onChange={(variableName) => onChange({ ...block, variableName })}
      />
    </div>
  );
}
